import React, {useEffect, useState} from "react";
import bemNames from "../utils/bemnames";
import {ReactComponent as PlaceholderSVG} from "../svg/placeholder-icon.svg";
import {ReactComponent as PlaySVG} from "../svg/play-arrow.svg";
import {generateCdnAssetsUrl, generateCdnProxyImageUrl, slugify} from "../utils/helpers";
import {
  EventStatus,
  IChannel,
  INextRoundData,
  IWidgetConfiguration,
  widgetConfigKeys,
  WidgetTypes
} from "../utils/types";
import {ChannelTypes, getChannelOutLink} from "../utils/channels";
import {_t} from '../utils/translations';
import {fetchNextRoundData} from "../api/match-center";
import Breadcrumbs from "./Sports/EventTopBanner/Breadcrumbs";
import EventTopBanner from "./MatchCenter/EventTopBanner";
import {getMeta, WidgetMetaTypes} from "../utils/widget-meta";
import {useGlobalState} from "../contexts/GlobalState";
import ChannelTrackingLink from "./ChannelTrackingLink";
import {asyncChannelClickTracker} from "../utils/traker";
import ImgWithDefault from "./ImgWithDefault";

const bem = bemNames.create('next-round-banner');

const SHOW_SERVICES_TIMEOUT = 200;
const HIDE_SERVICES_TIMEOUT = 100;

enum MS_VALUES {
  PER_SECOND = 1000,
  PER_MINUTE = PER_SECOND * 60,
  PER_HOUR = PER_MINUTE * 60,
  PER_DAY = PER_HOUR * 24,
}

interface IProps {
  outLinkFolder: string | null | undefined,
  isMobile: boolean,
  widgetId: number,
  competitionId?: number | null,
  eventId?: number,
  widgetTypeId: number
  componentName?: string;
  builderComponentId?: number;
}

const NextRoundBanner: React.FC<IProps> = ({
                                             outLinkFolder,
                                             isMobile,
                                             widgetId,
                                             competitionId,
                                             eventId,
                                             widgetTypeId,
                                             componentName,
                                             builderComponentId
                                           }) => {
  const [servicesOpened, setServicesOpened] = useState<boolean>(false);
  const [showingServices, setShowingServices] = useState<boolean>(false);
  const [nextRound, setNextRound] = useState<INextRoundData | null>(null);
  const [startTime, setStartTime] = useState<string>('');
  const [lateStart, setLateStart] = useState<boolean>(false);
  const [requestFailed, setRequestFailed] = useState<boolean>(false);
  const {globalState} = useGlobalState()
  const [backgroundUrl, setBackgroundUrl] = useState<string>(generateCdnAssetsUrl('mach-center-banner-background/football.png'));

  const getNextRound = () => {
    if (globalState.promotedProvider !== null) {
      if (globalState.promotedProvider.backgroundUrl !== null && globalState.promotedProvider.backgroundUrl.length > 0)
        setBackgroundUrl(globalState.promotedProvider.backgroundUrl);
    }
    fetchNextRoundData({
      widgetId: widgetId,
      competitionId: competitionId,
      eventId: eventId
    }).then((r: INextRoundData) => {
      setNextRound(r);
      setRequestFailed(false);
    }).catch(() => {
      setRequestFailed(true);
    });
  };

  const openOutLink = (channel: IChannel | null = null) => {
    if (outLinkFolder) {
      let channelId: number | undefined;
      if (channel === null) {
        if (nextRound) {
          channelId = nextRound.preferredChannel?.id;
        }
      } else {
        channelId = channel.id;
      }

      if (channelId) {
        const outLink = getChannelOutLink(outLinkFolder, channelId)

        // async track click
        if (outLink && outLink.length > 0) {
          asyncChannelClickTracker(
            widgetId,
            channelId,
            outLink,
            competitionId,
          );
        }

        window.open(outLink, '_blank', 'noreferrer')?.focus();
      }
    }
  };

  const dateDiff = (a: Date, b: Date, diffType: number) => {
    const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

    return Math.floor((utc2 - utc1) / diffType);
  }

  const isLive = () => {
    return nextRound?.status === EventStatus.Inprogress;
  }

  /*No way to add animation along with conditional rendering, used combination of width changing and conditional rendering,
  so need to display element when animation reach certain stage*/
  const toggleServices = () => {
    setShowingServices(!showingServices);
  };
  useEffect(() => {
    setTimeout(() => {
      setServicesOpened(showingServices);
    }, showingServices ? SHOW_SERVICES_TIMEOUT : HIDE_SERVICES_TIMEOUT)
  }, [showingServices]);

  useEffect(getNextRound, [widgetId, competitionId, eventId, globalState.promotedProvider]);

  useEffect(() => {
    if (nextRound !== null) {
      const curDate = new Date();
      const startDate = new Date(nextRound.startTime);

      let hoursDiff = dateDiff(curDate, startDate, MS_VALUES.PER_HOUR);
      let lateStart = false;
      if (hoursDiff >= 48) {
        lateStart = true;
      }
      setLateStart(lateStart);

      let prefix = '';
      let time = startDate.toLocaleString('default', {hour: 'numeric', minute: 'numeric', hour12: false});
      if (startDate.getDate() === curDate.getDate()) {
        prefix = 'today';
      } else if (startDate.getDate() === (curDate.getDate() + 1)) {
        prefix = 'tomorrow';
      } else {
        prefix = `${startDate.toLocaleString('default', {day: 'numeric', month: 'long'})}`;
      }
      setStartTime(`${prefix} at ${time}`);
    }
  }, [nextRound]);

  const bannerBorderStyle = {
    borderRadius: widgetTypeId !== WidgetTypes.TYPE_MATCH_CENTER ? '9px' : '0 0 var(--siw-mc-components-border-radius) var(--siw-mc-components-border-radius)'
  };

  const hideChannelLinks = globalState?.widget?.configuration?.find((item: IWidgetConfiguration) => item.key === widgetConfigKeys.hideChannelLinks)?.value;

  if (nextRound === null || requestFailed) {
    if (eventId !== undefined && globalState.widget !== null && builderComponentId !== undefined && componentName !== undefined) {
      return (
        <EventTopBanner
          componentName={componentName}
          eventID={eventId}
          languageID={globalState.widget.languageId}
          allowedProviders={globalState.widget.widgetProviders}
          outLinks={globalState.widget.outLinks}
          oddsFormat={globalState.widget.oddsFormat}
          isMobile={isMobile}
          showLinks={getMeta(WidgetMetaTypes.matchCenterBannerLinks) === 'show_links'}
          widgetId={widgetId}
          backgrounds={globalState.widget.backgrounds}
          builderComponentId={builderComponentId}
        />
      );
    } else return (
      <></>
    )
  } else {
    return (
      <div className={`${bem.e('banner-wrapper')} ${(isMobile) ? bem.e('mobile-banner-wrapper') : ''}`}>
        {widgetTypeId === WidgetTypes.TYPE_MATCH_CENTER && (
          <div className={bem.e('banner-header')}>
            <div className={bem.e('banner-header__banner-title')}>
              <span>
                {nextRound.leftTeamName} v {nextRound.rightTeamName}
              </span>
            </div>
            <div className={bem.e('breadcrumbs')}>
              <Breadcrumbs/>
            </div>
            {isLive() && !isMobile && (
              <div className={bem.e('banner-header__event-status')}>
                <span>{_t('Live')}</span>
              </div>
            )}
          </div>
        )}
        <div className={`${bem.e('banner')} ${(isMobile) ? bem.e('mobile-banner') : ''}`} onClick={() => openOutLink()}
             style={{backgroundImage: `url(${backgroundUrl})`, ...bannerBorderStyle}}>
          <div
            className={`${bem.e('description-container')} ${(isMobile) ? bem.e('mobile-description-container') : ''}`}>
            <div className={`${bem.e('title-container')} ${(isMobile) ? bem.e('mobile-title-container') : ''}`}>
              <span
                className={`${bem.e('title-container__title')} ${(isMobile) ? bem.e('mobile-title-container__title') : ''}`}>
                {nextRound.competitionName}
              </span>
              {hideChannelLinks !== "1" && nextRound.preferredChannel && outLinkFolder && <span className={bem.e(`title-container__status${isLive() ? ' live' : ''}`)}>
                <ChannelTrackingLink
                  outLink={getChannelOutLink(outLinkFolder, nextRound.preferredChannel.id)}
                  target="_blank"
                  rel="noreferrer"
                  channelId={nextRound.preferredChannel.id}
                  competitionId={competitionId ?? undefined}
                >
                  <>
                    {widgetTypeId === WidgetTypes.TYPE_LEAGUE_CENTER && isMobile
                      ? _t('Next round')
                      : (nextRound.status !== EventStatus.Inprogress
                        ? _t('Watch live')
                        : _t('In-play'))}
                  </>
                </ChannelTrackingLink>
              </span>}
            </div>
            {!isMobile && widgetTypeId === WidgetTypes.TYPE_LEAGUE_CENTER && (
              <div className={bem.e('description-container__description')}>
                <span className={bem.e('description-container__description__label')}>{_t('Next round:')}</span>
                <span className={bem.e('teams')}>{nextRound.leftTeamName} v {nextRound.rightTeamName}</span>
              </div>
            )}
          </div>
          <div className={bem.e('teams-container')}>
            <div className={bem.e('teams-container__teams')}>
              <div className={bem.e('teams-container__teams__team')}>
                <a href={`/competitor/${slugify(nextRound.leftTeamName)}`} onClick={(e) => e.stopPropagation()}
                   target='_blank' rel="noreferrer">
                  <ImgWithDefault src={generateCdnAssetsUrl(nextRound.leftTeamLogo)} alt={nextRound.leftTeamName}/>
                </a>
              </div>
              <div className={bem.e('teams-container__teams__delimiter')}>V</div>
              <div className={bem.e('teams-container__teams__team')}>
                <a href={`/competitor/${slugify(nextRound.rightTeamName)}`} onClick={(e) => e.stopPropagation()}
                   target='_blank' rel="noreferrer">
                  <ImgWithDefault src={generateCdnAssetsUrl(nextRound.rightTeamLogo)} alt={nextRound.rightTeamName}/>
                </a>
              </div>
            </div>
            <div className={bem.e('teams-container__description')}>
              {!lateStart && !isMobile && (
                <span>{_t('Kickoff')} {startTime}</span>
              )}
            </div>
            {isMobile && nextRound.status !== EventStatus.Inprogress && (
              <div className={bem.e('teams-container__counter-container')}>
                {!lateStart && (
                  <span className={bem.e('info-container__time-container__label')}>
                    {_t('time until live:')}
                  </span>
                )}
                <span className={bem.e('info-container__time-container__time')}>
                    {!lateStart ? nextRound.untilLive : startTime}
                </span>
              </div>
            )}
          </div>
          <div className={`${bem.e('info-container')} ${(isMobile) ? bem.e('mobile-info-container') : ''}`}>
            {isLive() ? (
              <div className={bem.e('info-container__game-info-container')}>
                <div className={bem.e('info-container__game-info-container__game-info')}>
                  <span className={bem.e('info-container__game-info-container__game-info label')}>
                    {_t('in-play:')}
                  </span>
                  <span className={bem.e('info-container__game-info-container__game-info value')}>
                    {nextRound.gameMoment}
                  </span>
                </div>
                <div className={bem.e('info-container__game-info-container__game-info')}>
                  <span className={bem.e('info-container__game-info-container__game-info label')}>
                    {_t('score:')}
                  </span>
                  <span className={bem.e('info-container__game-info-container__game-info value')}>
                    {nextRound.gameScore?.join(':')}
                  </span>
                </div>
              </div>
            ) : !isMobile && (
              <div className={bem.e('info-container__time-container')}>
                {!lateStart && (
                  <span className={bem.e('info-container__time-container__label')}>{_t('time until live:')}</span>
                )}
                <span className={bem.e('info-container__time-container__time')}>
                  {!lateStart ? nextRound.untilLive : startTime}
                </span>
              </div>
            )}
            <div className={bem.e('info-container__deals-wrapper')}>
              {!isMobile && (
                <div className={bem.e('info-container__deals-wrapper__deals-container')}>
                  {nextRound.preferredChannel?.clientChannelData &&
                    Object.keys(nextRound.preferredChannel.clientChannelData).map((key, index) => {
                      if (nextRound.preferredChannel?.clientChannelData && nextRound.preferredChannel?.clientChannelData[key] !== null) {
                        return (
                          <div className={bem.e('deal')} key={`deal_${index}`}>
                            <PlaceholderSVG fill="white"/>
                            <span>
                            {nextRound.preferredChannel?.clientChannelData ? nextRound.preferredChannel.clientChannelData[key] : ''}
                          </span>
                          </div>
                        );
                      } else return false;
                    })}
                </div>
              )}
              {hideChannelLinks !== "1" && <div className={bem.e('info-container__links-container')}>
                <div className={bem.e('info-container__links-container__buttons-container')}>
                  <div className={bem.e('btn')} onClick={() => openOutLink()}>
                    <PlaySVG fill="var(--siw-oo-header-text-color)"/>
                    <span>{_t('Stream Now')}</span>
                  </div>
                  {nextRound.preferredChannel?.logoUrl && (
                    <ImgWithDefault src={generateCdnAssetsUrl(nextRound.preferredChannel.logoUrl)} alt={nextRound.preferredChannel.name}/>
                  )}
                </div>
                {!isMobile && (
                  <div className={bem.e('info-container__links-container__links')}>
                    {nextRound.channels.length > 1 && (
                      <a href="#!" onClick={(e) => {
                        e.stopPropagation();
                        toggleServices();
                      }}>{_t('view all streaming services')}</a>
                    )}
                  </div>
                )}
              </div>}
            </div>
            {isMobile && (
              <div className={bem.e('info-container__links-container__links')}>
                {nextRound.channels.length > 1 && (
                  <a href="#!" onClick={(e) => {
                    e.stopPropagation();
                    toggleServices();
                  }}>{_t('view all streaming services')}</a>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Without libraries impossible to add transition for conditional rendering, used combination of width changing and
       conditional rendering, so there need 2 variables to not show content until animation reach certain stage */}
        <div className={`${bem.e('streaming-services-wrapper')} ${showingServices ? 'shown' : ''}`}>
          {servicesOpened && (
            <div className={bem.e('services-container')}>
              <div className={bem.e('services-container__close-btn')} onClick={toggleServices}>
                {_t('Close')}
              </div>
              <div className={bem.e('services-container__list')}>
                <div className={bem.e('services-container__list__title')}>
                  <span>{_t('Streaming Services')}</span>
                </div>
                <div className={bem.e('services-container__list__items')}>
                  {nextRound.channels.map((item, index) => {
                    if (item.channelType !== ChannelTypes.STREAMING_SERVICE)
                      return false;
                    return (
                      <div className={bem.e('services-container__list__items__item')} key={`service_${index}`}>
                        <div className={bem.e('services-container__list__items__item__info')}>
                          <div className={bem.e('services-container__list__items__item__info__service')}>
                            {item.logoUrl && (
                              <ImgWithDefault src={generateCdnProxyImageUrl(item.logoUrl)} alt={item.name}/>
                            )}
                            <span>{item.name}</span>
                          </div>
                          <div className={bem.e('services-container__list__items__item__info__deals')}>
                            <ul>
                              {item.clientChannelData &&
                                Object.keys(item.clientChannelData).map((key, index) => {
                                  return (
                                    <li key={`chan_${item.id}_deal_${index}`}>
                                      {item.clientChannelData ? item.clientChannelData[key] : ''}
                                    </li>
                                  );
                                })}
                            </ul>
                          </div>
                        </div>
                        <div className={bem.e('services-container__list__items__item__play')}
                             onClick={() => openOutLink(item)}>
                          <div className={bem.e('triangle')}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className={bem.e('services-container__list broadcasts')}>
                <div className={bem.e('services-container__list__title')}>
                  <span>{_t('Broadcasts')}</span>
                </div>
                <div className={bem.e('services-container__list__items')}>
                  {nextRound.channels.map((item, index) => {
                    if (item.channelType !== ChannelTypes.BROADCASTER)
                      return false;
                    return (
                      <div className={bem.e('services-container__list__items__item')} key={`broadcaster_${index}`}>
                        <div className={bem.e('services-container__list__items__item__info')}>
                          <div className={bem.e('services-container__list__items__item__info__service')}>
                            {item.logoUrl && (
                              <ImgWithDefault src={generateCdnProxyImageUrl(item.logoUrl)} alt={item.name}/>
                            )}
                            <span>{item.name}</span>
                          </div>
                          <div className={bem.e('services-container__list__items__item__info__deals')}>
                            <ul>
                              {item.clientChannelData && Object.keys(item.clientChannelData).map((key, dataIndex) => {
                                return (
                                  <li key={`chan_${item.id}_deal_${dataIndex}`}>
                                    {item.clientChannelData ? item.clientChannelData[key] : ''}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                        <div className={bem.e('services-container__list__items__item__play')}
                             onClick={() => openOutLink(item)}>
                          <div className={bem.e('triangle')}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default NextRoundBanner;
